/**
 * DOCUMENT-UPDATE-MODAL
 * Ask the user to generate the unique document
 */
import Modal from "@/components/modal"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import documentStore from "../../model/document.store"
import { createEmptyDocument, Document } from "../../model/document.types"
import physicalRiskStore from "@/entities/physical-risk/model/physical-risk.store"
import { useState } from "react"
import { PhysicalRisk } from "@/entities/physical-risk/model/physical-risk.types"
import PhysicalRiskEditModal from "@/entities/physical-risk/ui/modal/PhysicalRiskEditModal"
import CongratulationModal from "@/modals/congratulation.modal"
import { useNavigate } from "react-router-dom"
import { getPublicDocumentPath, openUrl } from "../../utils/document-file.utils"
import ModalConfirm from "@/components/modal-confirm"
import EstablishmentImportDocumentModal from "@/entities/establishment/ui/modal/EstablishmentImportDocumentModal"

interface Props {
  onClose: () => void
}

export default function DocumentUpdateModal({ onClose }: Props) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [editingRisk, setEditingRisk] = useState<PhysicalRisk | null>(null)
  const [created, setCreated] = useState<Document | null>(null)

  //Selectors
  const status = useAppSelector(documentStore.selectors.status)
  const _session = useAppSelector((state) => state._session)
  const establishment = useAppSelector((state) => state.establishment.active)
  const accountRisksSettings = useAppSelector((state) => state._session.accountRisksSettings)
  const riskFamilies = useAppSelector((state) => state.riskFamily.list)
  const physicalRisks = useAppSelector(physicalRiskStore.selectors.list)
  const preventionActions = useAppSelector((state) => state.preventionAction.list)
  const populations = useAppSelector((state) => state.population.list)
  const sites = useAppSelector((state) => state.site.list)
  const maps = useAppSelector((state) => state.map.list)
  const users = useAppSelector((state) => state.user.list)
  const documents = useAppSelector(documentStore.selectors.list)
  const generationDiff = useAppSelector(documentStore.selectors.generationDiff)
  const hasDuerp = documents.some((document) => document.type === "duerp")
  //Etablissement vide (ni document unique ni risque) : on propose d'abord d'importer celui qui existe hors de l'app
  const [step, setStep] = useState<"ASK_IMPORT" | "IMPORT" | "UPDATE">(hasDuerp || physicalRisks.length ? "UPDATE" : "ASK_IMPORT")

  function getDiffSymbol(type: string) {
    if (type === "added") return "+"
    if (type === "removed") return "-"
    return "~"
  }

  function getDiffColor(type: string) {
    if (type === "added") return "text-green-600"
    if (type === "removed") return "text-red-500"
    return "text-orange-500"
  }

  function getDiffLabel(item) {
    const risk = physicalRisks.find((risk) => risk.id === item.id)
    return risk?.cName || item.label
  }

  function openRisk(item) {
    if (item.scope !== "physicalRisk" || item.type === "removed") return
    const risk = physicalRisks.find((risk) => risk.id === item.id)
    if (!risk) return

    dispatch(physicalRiskStore.actions.setActive(risk))
    setEditingRisk(risk)
  }

  async function handleCreateDocument() {
    const establishmentId = _session.establishmentId ?? establishment.id

    const document = await dispatch(
      documentStore.thunks.createEntity(
        createEmptyDocument({
          type: "duerp",
          date: new Date(),
          EstablishmentId: establishmentId,
          content: {
            generatedAt: new Date().toISOString(),
            accountName: _session.accountName,
            establishment,
            accountRisksSettings,
            riskFamilies,
            physicalRisks,
            preventionActions,
            populations,
            sites,
            maps,
            users
          }
        })
      )
    ).unwrap()

    setCreated(document)
  }

  if (step === "ASK_IMPORT") {
    return (
      <ModalConfirm
        textBold="Souhaitez-vous importer votre document unique existant ?"
        onNo={() => setStep("UPDATE")}
        onYes={() => setStep("IMPORT")}
      />
    )
  }

  if (step === "IMPORT") {
    return <EstablishmentImportDocumentModal onClose={onClose} />
  }

  if (created) {
    return (
      <CongratulationModal
        text="Votre document unique a bien été mis à jour."
        onNext={() => {
          openUrl(getPublicDocumentPath(created, created.id))
          onClose()
        }}
        onNextText="Ouvrir le document"
        onClose={() => {
          onClose()
          navigate("/risks/documents")
        }}
        onCloseText="Fermer"
      />
    )
  }

  return (
    <>
      <Modal
        title="Mettre à jour mon document unique"
        isCloseButtonVisible
        onClose={onClose}
        onNext={handleCreateDocument}
        onNextCta="Mettre à jour"
        status={status}>
        {!hasDuerp ? (
          <p className="grey-t">
            {
              "Aucun document unique n’a encore été généré pour cet établissement. Vous pouvez créer une première version à partir des risques, lieux, populations et mesures de prévention actuellement renseignés."
            }
          </p>
        ) : generationDiff.length === 0 ? (
          <p className="grey-t">
            Aucun changement depuis la dernière version. Une nouvelle version sera générée avec les données actuelles
            et sera valable 12 mois.
          </p>
        ) : (
          <div>
            <p className="grey-t">Les changements suivants seront intégrés au document unique.</p>
            <div className="mt-10 text-sm">
              {generationDiff.map((item) => (
                <div
                  key={`${item.scope}-${item.type}-${item.id}`}
                  className={`flex leading-relaxed ${
                    item.scope === "physicalRisk" && item.type !== "removed" ? "_hover" : ""
                  }`}
                  onClick={() => openRisk(item)}>
                  <span className={`font-bold min-w-[20px] ${getDiffColor(item.type)}`}>
                    {getDiffSymbol(item.type)}
                  </span>
                  <span className="grey-t">
                    <b>{item.scope === "populationRps" ? "RPS" : "Risque"}</b> : {getDiffLabel(item)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>
      {editingRisk && <PhysicalRiskEditModal risk={editingRisk} onClose={() => setEditingRisk(null)} />}
    </>
  )
}
